import type React from 'react';

import { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { logIn } from '@/lib/api-client';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { motion } from 'motion/react';
import { useIsAuthenticated } from '@/hooks/use-is-authenticated';
import { useI18nContext } from '@/i18n/i18n-react';
import {
  AUTH_INPUT_CLASS,
  AuthPageLayout,
} from '@/pages/auth/auth-page-layout';
import { useLeaveAuth } from '@/pages/auth/auth-exit';

// Tap/hover feedback right on the link — motion starts the instant the
// merchant clicks "Criar conta", not only once the route has swapped.
const MotionLink = motion.create(Link);

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { LL } = useI18nContext();
  const [searchParams] = useSearchParams();
  const expired = searchParams.get('expired') === 'true';

  const isAuthenticated = useIsAuthenticated();
  const leave = useLeaveAuth();
  const signedIn = isAuthenticated.data === true;

  // Already signed in (e.g. landing from the emailed link): on to the
  // dashboard, the frame sliding away.
  useEffect(() => {
    if (signedIn) leave('/select-tenant');
  }, [signedIn, leave]);

  // Show expired link message
  useEffect(() => {
    if (expired) {
      toast.error(LL.login.linkExpiredTitle(), {
        description: LL.login.linkExpiredDescription(),
      });
    }
  }, [expired, LL]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await logIn(email);
      navigate(`/login/success?email=${encodeURIComponent(email)}`);
    } catch (error) {
      toast.error(LL.general.error(), {
        description:
          error instanceof Error
            ? error.message
            : LL.login.failedToSendMagicLink(),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isAuthenticated.isFetching || signedIn) {
    return <div></div>;
  }

  return (
    <AuthPageLayout>
      <AuthPageLayout.Header
        title={LL.login.signInTitle()}
        subtitle={LL.login.signInDescription()}
      />
      {expired && (
        <Alert variant="destructive" className="mb-4">
          <AlertTitle>{LL.login.linkExpiredTitle()}</AlertTitle>
          <AlertDescription>
            {LL.login.linkExpiredAlertDescription()}
          </AlertDescription>
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <AuthPageLayout.Content>
          <div className="space-y-2">
            <Label htmlFor="email" className="font-semibold text-[#15191E]">
              {LL.login.emailLabel()}
            </Label>
            <Input
              id="email"
              type="email"
              placeholder={LL.login.emailPlaceholder()}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={AUTH_INPUT_CLASS}
            />
          </div>
          <AuthPageLayout.Submit disabled={isSubmitting}>
            {isSubmitting ? LL.login.sending() : LL.login.sendMagicLinkButton()}
          </AuthPageLayout.Submit>
        </AuthPageLayout.Content>
      </form>
      <AuthPageLayout.Footer>
        <p className="text-sm text-[#4B5361]">
          {LL.login.noAccountQuestion()}{' '}
          <MotionLink
            to="/create-account"
            className="inline-block font-semibold text-[#155DFC]"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {LL.login.createAccountLink()}
          </MotionLink>
        </p>
      </AuthPageLayout.Footer>
    </AuthPageLayout>
  );
}
